import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from "react";
import { decode } from "@googlemaps/polyline-codec";
import { useTranslation } from "react-i18next";
import useSupercluster from "use-supercluster";

import { subtractSecondsFromTime, addSecondsToTime, CheckTimeString } from "shared/util/time";
import GenericModal from "components/shared/GenericModal";
import { ProgressBar } from "react-loader-spinner";
import GoogleMapReact from "google-map-react";
import RoomIcon from "@mui/icons-material/Room";
import SchoolIcon from "@mui/icons-material/School";
import { AuthContext } from "shared/context/auth-context";
import { DemoLeadCaptureContext } from "components/demo/DemoLeadCaptureContext";
import { getThemeName } from "config/ConfigUtils";
import { themeConfig } from "config/ThemeConfig";
import { useBackend } from "shared/hooks/backend-hook";
import { useUsageGuard } from "shared/hooks/useUsageGuard";
import { checkIsPickUp } from "shared/utility/types";
import { CLUSTERING_STORAGE_KEY } from "shared/utility/constants";
import Button from "shared/components/FormElements/Button";

import "./Map.css";

// #region Components

import Marker from "shared/components/UIElements/Marker";
import ClusterMarker from "shared/components/UIElements/ClusterMarker";
import ClusterPopup from "shared/components/UIElements/ClusterPopup";

// #region Helper Functions

/**
 * Calculates total service duration based on transfer times
 * For pickup: difference between school arrival time and first person's pickup time
 * For drop-off: difference between last person's drop-off time and institution departure time
 * Falls back to sum of direction durations if time format is invalid
 */
const calculateTotalDuration = (isPickUp, stops, transferStartFinishTime, directions) => {
  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 3600 + minutes * 60;
  };

  const getFallbackDuration = () => {
    return directions?.reduce((acc, direction) => acc + direction?.directionDetails?.duration, 0) || 0;
  };

  // Bir servis bacağı makul olarak bundan uzun süremez. Zaman-temelli hesap bunu aşıyorsa
  // saat verisi bozuktur (örn. dağıtmaya kopyalanmış sabah biniş saatleri: 07:55 − 17:00
  // gece-yarısı sarmalıyla 895 dk üretir) → rota bacak-toplamına düş.
  const MAX_REASONABLE_DURATION_SECONDS = 16 * 3600;

  const schoolTime = parseTimeToSeconds(transferStartFinishTime);

  if (isPickUp && stops?.length > 0) {
    const firstPersonTime = parseTimeToSeconds(stops[0]?.transferTime);
    if (schoolTime !== null && firstPersonTime !== null) {
      let duration = schoolTime - firstPersonTime;
      if (duration < 0) duration += 24 * 3600; // Handle midnight crossing
      if (duration <= MAX_REASONABLE_DURATION_SECONDS) return duration;
    }
    return getFallbackDuration();
  }

  if (!isPickUp && stops?.length > 0) {
    const lastPersonTime = parseTimeToSeconds(stops[stops.length - 1]?.transferTime);
    if (schoolTime !== null && lastPersonTime !== null) {
      let duration = lastPersonTime - schoolTime;
      if (duration < 0) duration += 24 * 3600; // Handle midnight crossing
      if (duration <= MAX_REASONABLE_DURATION_SECONDS) return duration;
    }
    return getFallbackDuration();
  }

  return getFallbackDuration();
};

const normalizeCoordinate = (value) => Number(Number(value).toFixed(6));

// Route Optimization isteğine gönderilebilecek geçerli bir konum mu? (backend isValidGeo paritesi)
// Sentinel/placeholder değerleri (-1.1,-1.1), (0,0) ve geçersiz sayılar elenir.
const isValidStopLocation = (location) => {
  if (!location) return false;
  const { lat, lng } = location;
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (lat === -1.1 && lng === -1.1) return false;
  if (lat === 0 && lng === 0) return false;
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
};

const coordsMatch = (a, b) => normalizeCoordinate(a) === normalizeCoordinate(b);

const attachLegCoordinates = (directionDetails, origin, destination) => {
  if (!directionDetails || !origin?.location || !destination?.location) {
    return directionDetails;
  }

  directionDetails.fromlat = origin.location.lat;
  directionDetails.fromlng = origin.location.lng;
  directionDetails.tolat = destination.location.lat;
  directionDetails.tolng = destination.location.lng;
  return directionDetails;
};

const findLegDirection = (directionsSource, fromLat, fromLng, toLat, toLng) =>
  directionsSource.find(
    (direction) =>
      coordsMatch(direction.fromlat, fromLat) &&
      coordsMatch(direction.fromlng, fromLng) &&
      coordsMatch(direction.tolat, toLat) &&
      coordsMatch(direction.tolng, toLng),
  );

// #endregion

// eslint-disable-next-line react/display-name
const Map = React.memo(
  ({
    stops,
    type,
    origin,
    enablePolyline,
    transferStartFinishTime,
    isFullScreen,
    onOptimize,
    idSuffix = "",
    // Opt-in: kayıtlı (DB) metrikler. Verilirse, kullanıcı bu oturumda bir değişiklik yapana kadar
    // (Optimize / ok tuşu / saati elle yazma) ekranda CANLI hesap yerine KAYITLI değer gösterilir;
    // kayıtlı değer yoksa "-" gösterilir. Verilmezse (dragger/analyzer) eski davranış: hep canlı.
    savedTotalDistanceKm,
    savedTotalDurationMin,
  }) => {
    const { getDirectionSingle, getDirectionsWithWaypoints, optimizeServiceRoute } = useBackend();
    const { checkUsageForMapsDirectionsSingle, checkUsage, UsageGuardModal } = useUsageGuard();
    const isPickUp = checkIsPickUp(type);

    //stops: id, name, location: {lat, lng}, transfertime
    //origin: id, name, location: {lat, lng}, transfertime

    const directionsREF = useRef([]);
    // distance: {from, to, distance, duration}

    const { t } = useTranslation();

    const auth = useContext(AuthContext);
    const { triggerOptimizeSuccess } = useContext(DemoLeadCaptureContext);
    const mapREF = useRef(null);
    const mapsREF = useRef(null);
    const polylinesREF = useRef([]);
    const polyLineLabelsREF = useRef([]);
    const initialFitDone = useRef(false);
    // Ok tuşlarıyla sıralama değiştiğinde saatlerin yeni sıraya göre yeniden hesaplanması
    // gerektiğini işaretler. Hesap, drawPolylines yeni komşuluk bacaklarını çektikten SONRA
    // yapılır (directionsREF'te bacak süreleri hazır olsun diye). İlk yüklemede asla
    // tetiklenmez — operatörün elle girdiği saatler korunur.
    const pendingTimeRecalcREF = useRef(false);

    const [loading, setLoading] = useState(false);
    const currentVersion = useRef(0);

    const [totalDistance, setTotalDistance] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [mapLoaded, setMapLoaded] = useState(false);

    // Kullanıcı bu oturumda metriği etkileyen bir değişiklik yaptı mı? (Optimize / ok tuşu / saati
    // elle yazma). true olunca ekranda KAYITLI değer yerine CANLI hesap gösterilir ve kaydedilir.
    const [metricsDirty, setMetricsDirty] = useState(false);
    // Kayıtlı metrik gösterimi yalnızca kayıtlı değer prop olarak verildiğinde devreye girer.
    const useSavedMetrics = savedTotalDistanceKm !== undefined || savedTotalDurationMin !== undefined;
    const showLiveMetrics = metricsDirty || !useSavedMetrics;

    // Demo: "Optimize" sonrası before/after iyileşme rozeti (sadece demo hesapları).
    // Rozet KALICI kalır (otomatik kaybolmaz); yalnızca yeniden optimize edilince güncellenir.
    const [optimizeDelta, setOptimizeDelta] = useState(null);
    const beforeOptimizeRef = useRef(null);
    const justOptimizedRef = useRef(false);

    useEffect(() => {
      if (!justOptimizedRef.current) return;
      justOptimizedRef.current = false;
      if (!auth.company?.isDemo) return;
      const before = beforeOptimizeRef.current;
      if (!before || isNaN(totalDistance) || isNaN(totalDuration)) return;
      const kmBefore = before.distance / 1000;
      const kmAfter = totalDistance / 1000;
      const minBefore = before.duration / 60;
      const minAfter = totalDuration / 60;
      // Yalnızca anlamlı bir iyileşme varsa göster.
      if (kmBefore > 0 && (kmAfter < kmBefore - 0.05 || minAfter < minBefore - 0.5)) {
        setOptimizeDelta({ kmBefore, kmAfter, minBefore, minAfter });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalDistance, totalDuration]);

    // eslint-disable-next-line no-unused-vars
    const [mapCenter, setMapCenter] = useState({ lat: 36.8969, lng: 30.7133 });
    // eslint-disable-next-line no-unused-vars
    const [mapZoom, setMapZoom] = useState(14);

    // Clustering state
    const [clusteringEnabled, setClusteringEnabled] = useState(() => {
      const stored = localStorage.getItem(CLUSTERING_STORAGE_KEY);
      return stored !== null ? stored === "true" : false; // Default: disabled
    });
    const [bounds, setBounds] = useState(null);
    const [currentZoom, setCurrentZoom] = useState(14);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [clusterStops, setClusterStops] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");

    const validStops = useMemo(() => (stops || []).filter((stop) => isValidStopLocation(stop?.location)), [stops]);

    const extendBoundsWithLocation = useCallback((bounds, location) => {
      if (!isValidStopLocation(location)) return false;
      bounds.extend(new mapsREF.current.LatLng(location.lat, location.lng));
      return true;
    }, []);

    // Convert stops to GeoJSON points for clustering
    const points = useMemo(() => {
      if (!stops || stops.length === 0) return [];
      return stops
        .map((stop, index) => ({ stop, index }))
        .filter(({ stop }) => isValidStopLocation(stop?.location))
        .map(({ stop, index }) => ({
          type: "Feature",
          properties: {
            cluster: false,
            stopId: stop.id,
            stopName: stop.name + (isPickUp && stop.transferTime ? " - " + stop.transferTime : ""),
            stopIndex: index + 1,
            originalStop: stop,
          },
          geometry: {
            type: "Point",
            coordinates: [stop.location.lng, stop.location.lat],
          },
        }));
    }, [stops, isPickUp]);

    // Supercluster hook for marker clustering
    const { clusters, supercluster } = useSupercluster({
      points,
      bounds,
      zoom: currentZoom,
      options: { radius: 75, maxZoom: 20 },
    });

    // Handle map change to update bounds and zoom
    const handleMapChange = ({ zoom, bounds: mapBounds }) => {
      setCurrentZoom(zoom);
      setBounds([mapBounds.nw.lng, mapBounds.se.lat, mapBounds.se.lng, mapBounds.nw.lat]);
    };

    // Handle cluster click to show popup with stop list
    const handleClusterClick = (clusterId, latitude, longitude) => {
      if (!supercluster) return;

      const leaves = supercluster.getLeaves(clusterId, Infinity);
      const stopsInCluster = leaves.map((leaf) => leaf.properties);
      setClusterStops(stopsInCluster);
      setSelectedCluster({ id: clusterId, lat: latitude, lng: longitude });
    };

    // Close cluster popup
    const handleCloseClusterPopup = () => {
      setSelectedCluster(null);
      setClusterStops([]);
    };

    // Toggle clustering and save to localStorage
    const handleClusteringToggle = (event) => {
      const newValue = event.target.checked;
      setClusteringEnabled(newValue);
      localStorage.setItem(CLUSTERING_STORAGE_KEY, String(newValue));
      // Close any open popup when toggling
      if (!newValue) {
        setSelectedCluster(null);
        setClusterStops([]);
      }
    };

    const handleApiLoaded = (map, maps) => {
      mapREF.current = map;
      mapsREF.current = maps;
      setMapLoaded(true);
      // drawPolylines();
    };

    const drawPolylines = useCallback(async () => {
      setLoading(true);
      const version = ++currentVersion.current; // Increment version

      if (!mapREF.current || !mapsREF.current) {
        setLoading(false);
        return;
      }

      if (!enablePolyline) {
        setLoading(false);
        return;
      }

      for (var k = 0; k < polylinesREF.current.length; k++) {
        polylinesREF.current[k].setMap(null);
      }
      for (var l = 0; l < polyLineLabelsREF.current.length; l++) {
        polyLineLabelsREF.current[l].setMap(null);
      }
      const currentPolylines = [];
      let totalDistanceData = 0;
      let totalDurationData = 0;

      let stopsToAsk = [];
      if (isPickUp) {
        stopsToAsk = stopsToAsk.concat(validStops);
        if (isValidStopLocation(origin?.location)) {
          stopsToAsk.push(origin);
        }
      } else {
        if (isValidStopLocation(origin?.location)) {
          stopsToAsk.push(origin);
        }
        stopsToAsk = stopsToAsk.concat(validStops);
      }

      const directionsToAsk = [];
      for (let i = 0; i < stopsToAsk.length - 1; i++) {
        const stop = stopsToAsk[i];
        const nextStop = stopsToAsk[i + 1];

        if (!isValidStopLocation(stop?.location) || !isValidStopLocation(nextStop?.location)) {
          continue;
        }

        directionsToAsk.push({
          origin: stop,
          destination: nextStop,
        });
      }

      if (directionsToAsk.length === 0) {
        pendingTimeRecalcREF.current = false;
        setLoading(false);
        return;
      }

      // Check usage allowance before making batch API calls
      const usageCheck = await checkUsageForMapsDirectionsSingle(directionsToAsk.length);
      if (!usageCheck.allowed) {
        // Bacaklar çekilemedi — bekleyen saat-yeniden-hesaplama isteğini iptal et
        // (bayrak asılı kalıp sonraki çizimde beklenmedik hesap tetiklemesin).
        pendingTimeRecalcREF.current = false;
        setLoading(false);
        // Still fit bounds to show stops on the map even without polylines
        if (!initialFitDone.current && mapREF.current && mapsREF.current) {
          const bounds = new mapsREF.current.LatLngBounds();
          let hasPoint = extendBoundsWithLocation(bounds, origin?.location);
          validStops.forEach((place) => {
            if (extendBoundsWithLocation(bounds, place?.location)) {
              hasPoint = true;
            }
          });
          if (hasPoint) {
            mapREF.current.fitBounds(bounds);
            initialFitDone.current = true;
          }
        }
        return;
      }

      const directions = await Promise.all(
        directionsToAsk.map(async (direction) => {
          const directionDetails = await getDirectionSingle(direction.origin, direction.destination, auth.token);
          attachLegCoordinates(directionDetails, direction.origin, direction.destination);
          return { directionDetails, id: direction.origin?.id };
        }),
      );

      for (var i = 0; i < directions.length; i++) {
        if (!directions[i]?.directionDetails?.overview_polyline) {
          setModalTitle(t("directionError"));
          setModalMessage(
            t("directionErrorDetailed", {
              originName: directionsToAsk[i].origin?.name || "",
              destinationName: directionsToAsk[i].destination?.name || "",
            }),
          );
          setShowModal(true);
          break;
        }
      }

      addtoDirections(directions);

      if (currentVersion.current !== version) {
        setLoading(false);
        return;
      }

      totalDistanceData = directions?.reduce((acc, direction) => acc + direction?.directionDetails?.distance, 0);
      //totalDurationData = directions?.reduce((acc, direction) => acc + direction?.directionDetails?.duration, 0);
      totalDurationData = calculateTotalDuration(isPickUp, stops, transferStartFinishTime, directions);

      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      for (var j = 0; j < directions.length; j++) {
        if (!directions[j].directionDetails?.overview_polyline?.points) {
          continue;
        }

        const directionPath = decode(directions[j].directionDetails.overview_polyline.points).map((coordinate) => ({
          lat: coordinate[0],
          lng: coordinate[1],
        }));

        // eslint-disable-next-line no-loop-func
        directionPath.forEach(({ lat, lng }) => {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
        });

        var polyline = new mapsREF.current.Polyline({
          path: directionPath,
          strokeColor: "#FF0000",
          strokeOpacity: 1,
          strokeWeight: 2,
          fillOpacity: 0,
          icons: [
            {
              icon: { path: mapsREF.current.SymbolPath.FORWARD_CLOSED_ARROW },
              offset: "100%",
              repeat: "30px",
            },
          ],
        });
        currentPolylines.push(polyline);
        polyline.setMap(mapREF.current);
      }

      if (!initialFitDone.current) {
        const bounds = new mapsREF.current.LatLngBounds();
        bounds.extend(new mapsREF.current.LatLng(maxLat, maxLng));
        bounds.extend(new mapsREF.current.LatLng(minLat, minLng));
        mapREF.current.fitBounds(bounds);
        initialFitDone.current = true;
      }

      setTotalDistance(totalDistanceData);
      setTotalDuration(totalDurationData);
      polylinesREF.current = currentPolylines;
      setLoading(false);

      // Ok tuşuyla sıralama değişmişse: yeni komşuluk bacakları artık directionsREF'te —
      // saatleri yeni sıraya göre yeniden hesapla ve yukarı bildir (ikinci çizim turunda
      // totals da yeni saatlerle güncellenir). Anchor (kurum saati) geçersizse hesap
      // null'lar üretip elle girilmiş saatleri silerdi — o durumda mevcut saatler korunur.
      if (pendingTimeRecalcREF.current) {
        pendingTimeRecalcREF.current = false;
        if (CheckTimeString(transferStartFinishTime)) {
          const freshLegs = directions
            .map((entry) => entry.directionDetails)
            .filter(
              (leg) =>
                leg?.fromlat !== null &&
                leg?.fromlat !== undefined &&
                leg?.duration !== null &&
                leg?.duration !== undefined,
            );
          onOptimize(calculateTransferTimes([...stops], freshLegs));
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enablePolyline, type, isPickUp, stops, validStops, origin, auth.token, t, extendBoundsWithLocation]);

    const fitBounds = useCallback(() => {
      if (!mapLoaded || !mapREF.current || !mapsREF.current) {
        return;
      }
      const bounds = new mapsREF.current.LatLngBounds();
      let hasPoint = extendBoundsWithLocation(bounds, origin?.location);
      validStops.forEach((place) => {
        if (extendBoundsWithLocation(bounds, place?.location)) {
          hasPoint = true;
        }
      });
      if (hasPoint) {
        mapREF.current.fitBounds(bounds);
      }
    }, [validStops, mapLoaded, origin, extendBoundsWithLocation]);

    useEffect(() => {
      if (!mapLoaded || mapsREF.current === null || mapREF.current === null) {
        return;
      }
      if (!initialFitDone.current) {
        fitBounds();
        initialFitDone.current = true;
      }
      drawPolylines();
    }, [stops, enablePolyline, drawPolylines, fitBounds, mapLoaded]);

    const addtoDirections = (directions) => {
      for (let i = 0; i < directions.length; i++) {
        const direction = directions[i].directionDetails;

        if (
          !direction?.fromlat ||
          !direction?.fromlng ||
          !direction?.tolat ||
          !direction?.tolng ||
          !direction?.duration ||
          !direction?.distance
        ) {
          continue;
        }

        const fromlat = direction.fromlat;
        const fromlng = direction.fromlng;
        const tolat = direction.tolat;
        const tolng = direction.tolng;
        const duration = direction.duration;
        const distance = direction.distance;

        const foundDirection = directionsREF.current.find(
          (direction) =>
            coordsMatch(direction.fromlat, fromlat) &&
            coordsMatch(direction.fromlng, fromlng) &&
            coordsMatch(direction.tolat, tolat) &&
            coordsMatch(direction.tolng, tolng),
        );

        if (foundDirection) {
          continue;
        }

        directionsREF.current.push({
          fromlat: fromlat,
          fromlng: fromlng,
          tolat: tolat,
          tolng: tolng,
          duration: duration,
          distance: distance,
        });
      }
    };

    const showDemoRoutePhones = (orderedStops) => {
      if (!auth.company?.isDemo) return;
      const people = (orderedStops || [])
        .map((stop) => ({
          name: String(stop?.name || "").trim(),
          time: stop?.transferTime ? String(stop.transferTime) : "",
          lat: Number(stop?.location?.lat),
          lng: Number(stop?.location?.lng),
        }))
        .filter((stop) => stop.name)
        .slice(0, 8);
      triggerOptimizeSuccess(
        people.length
          ? {
              schoolName: String(origin?.name || "").trim(),
              arrivalTime: transferStartFinishTime ? String(transferStartFinishTime) : "",
              schoolLat: Number(origin?.location?.lat),
              schoolLng: Number(origin?.location?.lng),
              isPickUp: Boolean(isPickUp),
              stops: people,
            }
          : null,
      );
    };

    // Optimize tuşu: önce Google Route Optimization API (uç noktayı optimal seçer: toplamada ilk,
    // dağıtmada son kişi). Herhangi bir nedenle kullanılamazsa (yapılandırma yok, hata, çözüm yok,
    // <2 geçerli durak) eski istemci-tarafı optimizasyona (legacyOptimizeHandler) güvenli şekilde düşer.
    const optimizeHandler2 = async () => {
      const handledByRouteOptimization = await optimizeViaRouteOptimization();
      if (!handledByRouteOptimization) {
        await legacyOptimizeHandler();
      }
    };

    // Route Optimization akışı. Dönüş: true → optimizasyon ele alındı (başarı ya da kota engeli),
    // false → çağıran legacy akışına düşmeli.
    const optimizeViaRouteOptimization = async () => {
      // Okul veya yeterli geçerli durak yoksa optimize edilecek bir şey yok → legacy ile aynı davran.
      if (!isValidStopLocation(origin?.location)) return false;
      const validStops = (stops || []).filter((s) => isValidStopLocation(s?.location));
      if (validStops.length < 2) return false;

      setLoading(true);

      // Demo: optimize öncesi km/dk değerlerini yakala (sonra iyileşme rozetini göstermek için).
      if (auth.company?.isDemo) {
        beforeOptimizeRef.current = { distance: totalDistance, duration: totalDuration };
        justOptimizedRef.current = true;
      }

      // Kota kontrolü (Route Optimization = 1 çağrı). Kota yoksa modal guard tarafından gösterilir;
      // bu durumda legacy'ye düşüp N çağrı harcamamak için "ele alındı" (true) döneriz.
      const usageCheck = await checkUsage("MAPS__ROUTE_OPTIMIZATION", 1);
      if (!usageCheck.allowed) {
        setLoading(false);
        return true;
      }

      const originPayload = { location: { lat: origin.location.lat, lng: origin.location.lng }, id: origin.id };
      const stopsPayload = validStops.map((s) => ({
        location: { lat: s.location.lat, lng: s.location.lng },
        id: s.id,
      }));

      const result = await optimizeServiceRoute(originPayload, stopsPayload, isPickUp, transferStartFinishTime);

      if (!result || !Array.isArray(result.orderedStops) || result.orderedStops.length === 0) {
        // Servis kullanılamadı / çözüm yok → legacy akışına düş.
        setLoading(false);
        justOptimizedRef.current = false; // legacy kendi yakalamasını yapacak
        return false;
      }

      // Optimize sıraya göre yeni durak listesi kur; optimizer'ın döndürmediği (atlanan / geçersiz
      // konumlu) duraklar orijinal sıralarını koruyarak SONA eklenir → yolcu kaybı olmaz.
      // NOT: Bu bileşenin adı `Map` olduğu için global Map() gölgelenir; düz nesne + Set kullanıyoruz.
      const stopById = {};
      (stops || []).forEach((s) => {
        stopById[String(s.id)] = s;
      });
      const usedIds = new Set();
      const newStops = [];
      for (const ordered of result.orderedStops) {
        const key = String(ordered.id);
        const stop = stopById[key];
        if (!stop || usedIds.has(key)) continue;
        usedIds.add(key);
        newStops.push({
          ...stop,
          transferTime: ordered.transferTime !== null ? ordered.transferTime : stop.transferTime,
        });
      }
      for (const stop of stops || []) {
        if (!usedIds.has(String(stop.id))) newStops.push(stop);
      }

      setMetricsDirty(true);
      onOptimize(newStops);
      showDemoRoutePhones(newStops);
      // setLoading(false) BİLEREK çağrılmıyor: onOptimize → stops değişimi → drawPolylines totalleri
      // yeniden hesaplayıp loading'i kapatır (legacy akışla aynı davranış).
      return true;
    };

    const legacyOptimizeHandler = async () => {
      if (!isValidStopLocation(origin?.location) || validStops.length === 0) {
        return;
      }

      setLoading(true);

      // Demo: optimize öncesi km/dk değerlerini yakala (sonra iyileşme rozetini göstermek için).
      if (auth.company?.isDemo) {
        beforeOptimizeRef.current = { distance: totalDistance, duration: totalDuration };
        justOptimizedRef.current = true;
      }

      // Check usage allowance before making batch API calls
      // We need stops.length calls for directionsToOrigin + potentially 1 more for waypoints
      const estimatedCalls = validStops.length;
      const usageCheck = await checkUsageForMapsDirectionsSingle(estimatedCalls);
      if (!usageCheck.allowed) {
        setLoading(false);
        return;
      }

      const directionsToOrigin = await Promise.all(
        validStops.map(async (stop) => {
          if (isPickUp) {
            const directionDetails = await getDirectionSingle(stop, origin, auth.token);
            return { directionDetails, id: stop.id };
          } else {
            const directionDetails = await getDirectionSingle(origin, stop, auth.token);
            return { directionDetails, id: stop.id };
          }
        }),
      );

      directionsToOrigin.sort((a, b) => b.directionDetails.distance - a.directionDetails.distance);
      const farthestId = directionsToOrigin[0].id;

      const farthestStop = validStops.find((stop) => stop.id === farthestId);
      if (!farthestStop || !isValidStopLocation(origin?.location)) {
        setLoading(false);
        return;
      }

      const startingPoint = {
        location: isPickUp ? farthestStop.location : origin.location,
        id: isPickUp ? farthestStop.id : origin.id,
      };
      const endPoint = {
        location: isPickUp ? origin.location : farthestStop.location,
        id: isPickUp ? origin.id : farthestStop.id,
      };

      const waypoints = validStops
        .filter((stop) => stop.id !== farthestId)
        .map((stop) => ({ location: stop.location, id: stop.id }));

      let directionDetails = [];
      if (waypoints.length > 0) {
        directionDetails = await getDirectionsWithWaypoints(startingPoint, endPoint, waypoints, auth.token);
      } else {
        const currentDirection = await getDirectionSingle(startingPoint, endPoint, auth.token);
        directionDetails = [{ currentDirection, id: startingPoint.id }];
      }

      const directionsToAdd = directionDetails.map((direction) => {
        return {
          directionDetails: direction,
        };
      });

      addtoDirections(directionsToAdd);

      let newStops = [];

      for (let i = 0; i < directionDetails.length; i++) {
        const stop = stops.find((stop) => stop.id === directionDetails[i].id);
        if (stop) {
          newStops.push(stop);
        }
      }

      if (!isPickUp) {
        newStops.push(stops.find((stop) => stop.id === farthestId));
      }

      const calculatedNewStops = calculateTransferTimes(newStops);

      setMetricsDirty(true);
      onOptimize(calculatedNewStops);
      showDemoRoutePhones(calculatedNewStops);
    };

    const calculateTransferTimes = (newStops, directionsOverride = null) => {
      const directionsSource = directionsOverride || directionsREF.current;
      const routableStops = newStops.filter((stop) => isValidStopLocation(stop?.location));
      if (!isValidStopLocation(origin?.location) || routableStops.length === 0) {
        return newStops;
      }

      let pickupbeforeseconds = 0;
      let currentStops = [...newStops];
      if (isPickUp) {
        const lastStop = routableStops[routableStops.length - 1];
        const lastDirection = findLegDirection(
          directionsSource,
          lastStop.location.lat,
          lastStop.location.lng,
          origin.location.lat,
          origin.location.lng,
        );

        const lastDuration = lastDirection?.duration || 0;
        pickupbeforeseconds = lastDuration;
        lastStop.pickupbeforeseconds = pickupbeforeseconds;
        lastStop.transferTime = subtractSecondsFromTime(transferStartFinishTime, pickupbeforeseconds);

        for (var z = routableStops.length - 1; z > 0; z--) {
          const stop = routableStops[z - 1];
          const nextStop = routableStops[z];

          const currentDirection = findLegDirection(
            directionsSource,
            stop.location.lat,
            stop.location.lng,
            nextStop.location.lat,
            nextStop.location.lng,
          );

          const currentDuration = currentDirection?.duration || 0;

          pickupbeforeseconds += currentDuration;
          stop.pickupbeforeseconds = pickupbeforeseconds;
          stop.transferTime = subtractSecondsFromTime(transferStartFinishTime, pickupbeforeseconds);
        }
      } else {
        // Drop-off: Origin'den başla, ileriye doğru hesapla
        let dropoffAfterSeconds = 0;

        // Origin'den ilk durağa olan süreyi bul
        const firstDirection = findLegDirection(
          directionsSource,
          origin.location.lat,
          origin.location.lng,
          routableStops[0].location.lat,
          routableStops[0].location.lng,
        );

        dropoffAfterSeconds = firstDirection?.duration || 0;
        routableStops[0].transferTime = addSecondsToTime(transferStartFinishTime, dropoffAfterSeconds);

        // Kalan duraklar için ileriye doğru hesapla
        for (let z = 1; z < routableStops.length; z++) {
          const prevStop = routableStops[z - 1];
          const currentStop = routableStops[z];

          const currentDirection = findLegDirection(
            directionsSource,
            prevStop.location.lat,
            prevStop.location.lng,
            currentStop.location.lat,
            currentStop.location.lng,
          );

          dropoffAfterSeconds += currentDirection?.duration || 0;
          currentStop.transferTime = addSecondsToTime(transferStartFinishTime, dropoffAfterSeconds);
        }
      }
      return currentStops;
    };

    const personPickupTimeChangeHandler = (event, personId) => {
      const person = stops.find((person) => person.id === personId);
      person.transferTime = event.target.value;
      setMetricsDirty(true);
      onOptimize(stops);
    };

    const personUpHandler = (personId) => {
      const pos = stops.map((person) => person.id).indexOf(personId);
      let newPeople = [];
      for (let i = 0; i < stops.length; i++) {
        if (i === pos && i !== 0) {
          const prev = newPeople.pop();
          newPeople.push(stops[i]);
          newPeople.push(prev);
          continue;
        } else {
          newPeople.push(stops[i]);
        }
      }
      // Müşteri onayı: sıra değişince saatler yeni sıraya göre yeniden hesaplansın.
      pendingTimeRecalcREF.current = true;
      setMetricsDirty(true);
      onOptimize(newPeople);
    };

    const personDownHandler = (personId) => {
      const pos = stops.findIndex((person) => person.id === personId);

      if (pos < stops.length - 1) {
        const newPeople = [...stops];
        [newPeople[pos], newPeople[pos + 1]] = [newPeople[pos + 1], newPeople[pos]];
        // Müşteri onayı: sıra değişince saatler yeni sıraya göre yeniden hesaplansın.
        pendingTimeRecalcREF.current = true;
        setMetricsDirty(true);
        onOptimize(newPeople);
      }
    };

    return (
      <React.Fragment>
        <GenericModal
          message={modalMessage}
          title={modalTitle}
          onClose={() => setShowModal(false)}
          showModal={showModal}
        />
        <UsageGuardModal />

        {clusteringEnabled && selectedCluster && clusterStops.length > 0 && (
          <ClusterPopup stops={clusterStops} onClose={handleCloseClusterPopup} />
        )}

        {enablePolyline && type && (
          <div style={{ width: "400px" }}>
            <div style={{ display: "flex", columnGap: "20px", marginTop: "10px", marginBottom: "5px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  columnGap: "5px",
                  height: "40px",
                }}
              >
                <span>
                  {t("distance")}
                  {":"}
                </span>
                {loading && (
                  <ProgressBar
                    visible={true}
                    height="40"
                    width="60"
                    color="#4fa94d"
                    ariaLabel="progress-bar-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                )}
                {!loading && (
                  <b id={"totalDistance" + idSuffix}>
                    {" "}
                    {showLiveMetrics
                      ? !isNaN(totalDistance)
                        ? `${(totalDistance / 1000).toFixed(1)} ${t("km")}`
                        : "-"
                      : Number(savedTotalDistanceKm) > 0
                        ? `${Number(savedTotalDistanceKm).toFixed(1)} ${t("km")}`
                        : "-"}{" "}
                  </b>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  columnGap: "5px",
                  height: "40px",
                }}
              >
                <span>
                  {t("duration")}
                  {":"}
                </span>
                {loading && (
                  <ProgressBar
                    visible={true}
                    height="40"
                    width="60"
                    color="#4fa94d"
                    ariaLabel="progress-bar-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                )}
                {!loading && (
                  <b id={"totalDuration" + idSuffix}>
                    {" "}
                    {showLiveMetrics
                      ? !isNaN(totalDuration)
                        ? `${(totalDuration / 60).toFixed(0)} ${t("minutes")}`
                        : "-"
                      : Number(savedTotalDurationMin) > 0
                        ? `${Math.round(Number(savedTotalDurationMin))} ${t("minutes")}`
                        : "-"}{" "}
                  </b>
                )}
              </div>
            </div>
            {optimizeDelta && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "10px",
                  background: "#1d3469",
                  color: "#fff",
                  padding: "9px 13px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 600,
                  lineHeight: 1.3,
                  boxShadow: "0 8px 22px rgba(29,52,105,0.25)",
                  animation: "fadeIn 0.4s ease",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "#fbe122",
                    color: "#1d3469",
                    flexShrink: 0,
                    fontSize: "12px",
                  }}
                  aria-hidden="true"
                >
                  ✓
                </span>
                <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.35 }}>
                  <span>
                    {t("optimizeDeltaBadge", { brand: themeConfig[auth.company?.brand]?.name || getThemeName() })}
                  </span>
                  <span>
                    {optimizeDelta.kmBefore.toFixed(1)} → {optimizeDelta.kmAfter.toFixed(1)} {t("km")}{" "}
                    <span style={{ color: "#fbe122" }}>
                      (−{Math.max(0, Math.round((1 - optimizeDelta.kmAfter / optimizeDelta.kmBefore) * 100))}%)
                    </span>
                  </span>
                </span>
              </div>
            )}
            {!isPickUp && (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#aaa",
                    fontWeight: "bold",
                    marginRight: "5px",
                    marginTop: "10px",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    padding: "10px",
                    width: "100%",
                  }}
                >
                  {origin?.name || ""} - {transferStartFinishTime}
                </div>
              </div>
            )}

            {stops.map((person, index) => (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                  alignItems: "center",
                }}
                key={"calculatedUsers" + index}
              >
                <div style={{ display: "flex", width: "100%", flexDirection: "row", alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <button
                      style={{
                        alignSelf: "flex-start",
                        backgroundColor: "black",
                        padding: "5px",
                        marginBottom: "5px",
                      }}
                      type="button"
                      onClick={() => personUpHandler(person.id)}
                    >
                      <div className="arrow-up"></div>
                    </button>
                    <button
                      style={{ alignSelf: "flex-start", backgroundColor: "black", padding: "5px" }}
                      type="button"
                      onClick={() => personDownHandler(person.id)}
                    >
                      <div className="arrow-down"></div>
                    </button>
                  </div>

                  <div
                    style={{
                      backgroundColor: "#bbb",
                      padding: "10px",
                      margin: "5px",
                      width: "100%",
                      borderRadius: "5px",
                    }}
                  >
                    {index + 1 + ". " + person.name}
                  </div>

                  {/* Saat kutusu her iki yönde de görünür: toplamada alınma, dağıtmada iniş saati.
                    Dağıtma saatleri operatör-içi bilgidir — sürücü ve yolcu uygulamalarında gösterilmez. */}
                  <input
                    style={{
                      height: "30px",
                      width: "48px",
                      borderRadius: "1px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                      marginRight: "5px",
                      outlineColor: "black",
                      borderColor: "black",
                    }}
                    type="text"
                    key={person.id + "-transferTime"}
                    id={person.id + "-transferTime"}
                    onInput={(e) => personPickupTimeChangeHandler(e, person.id)}
                    value={person?.transferTime || ""}
                  />
                </div>
                {person.polylineLabel && (
                  <div style={{ marginBottom: "10px" }}>
                    {" "}
                    {">> "}
                    {person?.polylineLabel} {" >>"}
                  </div>
                )}
              </div>
            ))}
            {isPickUp && (
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#aaa",
                    fontWeight: "bold",
                    marginRight: "5px",
                    marginTop: "10px",
                    borderRadius: "5px",
                    padding: "10px",
                    width: "100%",
                  }}
                >
                  {origin?.name || ""} {transferStartFinishTime && <span>- {transferStartFinishTime} </span>}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={isFullScreen ? "map-with-transfers-container" : "map-with-transfers-container"}>
          {enablePolyline && !isNaN(totalDistance) && !isNaN(totalDuration) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                columnGap: "20px",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}>
                  <input
                    type="checkbox"
                    checked={clusteringEnabled}
                    onChange={handleClusteringToggle}
                    style={{ width: "16px", height: "16px", marginRight: "8px", cursor: "pointer" }}
                  />
                  {t("groupNearbyStops")}
                </label>
              </div>
              <div data-demo-coach="optimize">
                <Button type="button" danger disabled={loading} onClick={optimizeHandler2}>
                  {t("optimize")}
                </Button>
              </div>
            </div>
          )}

          <div className={isFullScreen ? "shared-service-map-container-big" : "shared-service-map-container"}>
            <GoogleMapReact
              bootstrapURLKeys={{
                language: "tr",
                region: "tr",
                libraries: ["drawing", "geometry", "encoding"],
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
              onChange={handleMapChange}
              options={function (maps) {
                return {
                  mapTypeControl: true,
                  mapTypeId: "roadmap",
                  mapTypeControlOptions: {
                    mapTypeIds: ["satellite", "roadmap"],
                    style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    position: maps.ControlPosition.TOP_LEFT,
                  },
                };
              }}
              defaultCenter={mapCenter}
              defaultZoom={mapZoom}
            >
              {origin && isValidStopLocation(origin?.location) && (
                <Marker
                  Icon={SchoolIcon}
                  lat={origin.location.lat}
                  lng={origin.location.lng}
                  info={origin?.name || ""}
                  initialShow
                />
              )}

              {clusteringEnabled
                ? clusters.map((cluster) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const { cluster: isCluster, point_count: pointCount } = cluster.properties;

                    if (isCluster) {
                      return (
                        <ClusterMarker
                          key={`cluster-${cluster.id}`}
                          lat={latitude}
                          lng={longitude}
                          pointCount={pointCount}
                          onClick={() => handleClusterClick(cluster.id, latitude, longitude)}
                        />
                      );
                    }

                    // Single marker (not clustered)
                    const { stopIndex, stopName } = cluster.properties;
                    return (
                      <Marker
                        Icon={RoomIcon}
                        lat={latitude}
                        lng={longitude}
                        key={`stop-${cluster.properties.stopId}`}
                        info={stopIndex + ". " + stopName}
                        initialShow
                        alwaysShowMarker={true}
                        alwaysBlue={true}
                      />
                    );
                  })
                : validStops.map((stop) => {
                    const index = stops.findIndex((item) => item.id === stop.id);
                    return (
                      <Marker
                        Icon={RoomIcon}
                        lat={stop.location.lat}
                        lng={stop.location.lng}
                        key={stop.id}
                        info={
                          index +
                          1 +
                          ". " +
                          stop.name +
                          (isPickUp && stop.transferTime ? " - " + stop.transferTime : "")
                        }
                        initialShow
                        alwaysShowMarker={true}
                        alwaysBlue={true}
                      />
                    );
                  })}
            </GoogleMapReact>
          </div>
        </div>
      </React.Fragment>
    );
  },
);

export default Map;
